import apiClient from './apiClient';

export interface AIRequestDto {
  prompt: string;
  context?: string;
  systemMessage?: string;
  maxTokens?: number;
}

export interface AIResponseDto {
  success: boolean;
  content: string;
  error?: string;
  tokensUsed: number;
}

export interface AIFormFillRequestDto {
  dictation: string;
  formType: string;
}

export interface AIQuizGenerateRequestDto {
  documentContent: string;
  questionCount?: number;
}

export interface AuditQuestionDraftDto {
  questionText: string;
  questionType: 'FreeText' | 'MultiChoice';
  options: string[];
}

export interface PiaInformationAssetDraftDto {
  assetName: string;
  classification: string;
  description: string;
  retentionPeriod: string;
}

export interface PiaPrincipleAssessmentDraftDto {
  principle: string;
  compliant: string;
  notes: string;
  riskLevel: string;
  mitigationActions: string;
}

export interface PiaDraftDto {
  title?: string | null;
  date?: string | null;
  classification?: string | null;
  introduction?: string | null;
  purposes?: string | null;
  scopeAndExclusions?: string | null;
  contextObjectivesAndBenefits?: string | null;
  roles?: string | null;
  systemProcessDescription?: string | null;
  informationAssets: PiaInformationAssetDraftDto[];
  informationFlow?: string | null;
  thirdPartyServicesAndCrossBorder?: string | null;
  securitySafeguardsAndControls?: string | null;
  selectedPrivacyStandard?: string | null;
  principleAssessments: PiaPrincipleAssessmentDraftDto[];
  riskAssessments?: string | null;
}

export interface AISearchRequestDto {
  query: string;
  context?: string;
  sources?: AIDocumentSourceDto[];
}

export interface AIDocumentSourceDto {
  documentId: string;
  title: string;
  documentType: string;
  versionNumber: number;
  linkUrl: string;
}

export interface AISearchResponseDto extends AIResponseDto {
  sources: AIDocumentSourceDto[];
}

export interface AISourceLibraryItemDto {
  sourceId: string;
  sourceType: 'url' | 'file';
  label: string;
  url?: string;
  storedFileName?: string;
  contentType?: string;
  fileSize?: number;
  createdByUserId: string;
  createdByName?: string;
  organisationId?: string;
  subOrganisationId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAISourceLibraryItemDto {
  sourceType: 'url' | 'file';
  label: string;
  url?: string;
  storedFileName?: string;
  contentType?: string;
  fileSize?: number;
  organisationId?: string | null;
  subOrganisationId?: string | null;
}

export interface AIPromptLibraryItemDto {
  promptId: string;
  title: string;
  promptText: string;
  createdByUserId: string;
  createdByName?: string;
  organisationId?: string;
  subOrganisationId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAIPromptLibraryItemDto {
  title: string;
  promptText: string;
  organisationId?: string | null;
  subOrganisationId?: string | null;
}

export interface UpdateAIPromptLibraryItemDto {
  title: string;
  promptText: string;
}

export interface AIRagSearchRequestDto {
  query: string;
  organisationId?: string | null;
  subOrganisationId?: string | null;
  maxResults?: number;
  includeAnswer?: boolean;
  includeGraphContext?: boolean;
  graphDepth?: number;
}

export interface AIRagSourceDto {
  sourceId: string;
  chunkId: string;
  sourceType: string;
  entityType: string;
  entityId: string;
  documentId?: string;
  title: string;
  sectionTitle?: string;
  versionNumber?: number;
  excerpt: string;
  linkUrl: string;
  score: number;
}

export interface AIRagSearchResponseDto extends AIResponseDto {
  retrievalMode: string;
  sources: AIRagSourceDto[];
  graphTrace: AIRagGraphTraceDto[];
  structuredResults?: AIRagStructuredResultDto[];
  structuredResultTotal?: number;
  embeddingLatencyMs?: number;
  qdrantLatencyMs?: number;
  retrievalLatencyMs?: number;
  generationLatencyMs?: number;
  totalLatencyMs?: number;
  embeddingCalls?: number;
  estimatedEmbeddingTokens?: number;
}

export interface AIRagStructuredResultDto {
  entityId: string;
  entityType: string;
  referenceNumber: string;
  title: string;
  status: string;
  severity?: string;
  eventDate?: string;
  summary?: string;
  linkUrl: string;
}

export interface AIRagIndexResponseDto {
  success: boolean;
  sourcesIndexed: number;
  chunksIndexed: number;
  embeddingCalls?: number;
  indexingTimeMs?: number;
  message: string;
  error?: string;
}

export interface AIRagIndexJobDto {
  jobId: string;
  action: string;
  entityType: string;
  entityId: string;
  userId: string;
  organisationId?: string;
  subOrganisationId?: string;
  status: string;
  attempts: number;
  availableAt: string;
  lockedAt?: string;
  completedAt?: string;
  lastError?: string;
  sourcesIndexed?: number;
  chunksIndexed?: number;
  embeddingCalls?: number;
  indexingTimeMs?: number;
  createdAt: string;
  updatedAt: string;
}

export interface AIRagIndexJobQueryDto {
  status?: string;
  entityType?: string;
  page?: number;
  pageSize?: number;
}

export interface AIRagVectorStoreStatusDto {
  enabled: boolean;
  reachable: boolean;
  provider: string;
  collectionName: string;
  collectionExists: boolean;
  status?: string;
  pointsCount?: number;
  indexedVectorsCount?: number;
  vectorSize?: number;
  error?: string;
  graphStore?: AIRagGraphStoreStatusDto;
}

export interface AIRagGraphStoreStatusDto {
  enabled: boolean;
  reachable: boolean;
  provider: string;
  nodeCount?: number;
  latencyMs: number;
  error?: string;
}

export interface AIRagOperationMetricDto {
  metricId: string;
  operation: string;
  provider: string;
  organisationId?: string;
  subOrganisationId?: string;
  entityType?: string;
  entityId?: string;
  success: boolean;
  sourceCount: number;
  chunkCount: number;
  embeddingCalls: number;
  estimatedEmbeddingTokens: number;
  embeddingLatencyMs: number;
  qdrantLatencyMs: number;
  retrievalLatencyMs: number;
  generationLatencyMs: number;
  totalLatencyMs: number;
  error?: string;
  createdAt: string;
}

export interface AIRagMetricsSummaryDto {
  lastReindexAt?: string;
  searchCount24h: number;
  averageSearchLatencyMs: number;
  averageQdrantLatencyMs: number;
  embeddingCalls24h: number;
  estimatedEmbeddingTokens24h: number;
  recentOperations: AIRagOperationMetricDto[];
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface AIRagGraphNodeDto {
  nodeId: string;
  nodeType: string;
  label: string;
  sourceId?: string;
  entityId?: string;
  degree: number;
  linkUrl?: string;
}

export interface AIRagGraphEdgeDto {
  edgeId: string;
  fromNodeId: string;
  toNodeId: string;
  relationshipType: string;
  weight: number;
}

export interface AIRagGraphDto {
  nodes: AIRagGraphNodeDto[];
  edges: AIRagGraphEdgeDto[];
  nodeTypes: string[];
  relationshipTypes: string[];
}

export interface AIRagGraphTraceDto {
  sourceId: string;
  title: string;
  relationships: string[];
}

export interface AIRagGraphRequestDto {
  organisationId?: string | null;
  subOrganisationId?: string | null;
  search?: string;
  centerNodeId?: string;
  depth?: number;
  limit?: number;
  nodeTypes?: string[];
  relationshipTypes?: string[];
  entityIds?: string[];
  includeNeighborhood?: boolean;
}

const aiApi = {
  generateContent: (request: AIRequestDto) =>
    apiClient.post<{ data: AIResponseDto }>('/api/ai/generate', request),

  summarizeContent: (content: string) =>
    apiClient.post<{ data: AIResponseDto }>('/api/ai/summarize', content, {
      headers: { 'Content-Type': 'application/json' },
    }),

  generateQuiz: (request: AIQuizGenerateRequestDto) =>
    apiClient.post<{ data: AIResponseDto }>('/api/ai/generate-quiz', request),

  generateAuditQuestionsFromPdf: (file: File, questionCount: number) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('questionCount', String(questionCount));
    return apiClient.post<{ data: AuditQuestionDraftDto[] }>('/api/ai/audit-questions/from-pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  generatePiaDraft: (intakeText: string) =>
    apiClient.post<{ data: PiaDraftDto }>('/api/ai/pia-draft', { intakeText }),

  generatePiaDraftFromPdf: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<{ data: PiaDraftDto }>('/api/ai/pia-draft/from-pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  autoFillForm: (request: AIFormFillRequestDto) =>
    apiClient.post<{ data: AIResponseDto }>('/api/ai/auto-fill', request),

  searchWithAI: (request: AISearchRequestDto) =>
    apiClient.post<{ data: AISearchResponseDto }>('/api/ai/search', request),

  searchRag: (request: AIRagSearchRequestDto) =>
    apiClient.post<{ data: AIRagSearchResponseDto }>('/api/ai/rag/search', request),

  reindexRag: (params?: { organisationId?: string | null; subOrganisationId?: string | null }) =>
    apiClient.post<{ data: AIRagIndexJobDto }>('/api/ai/rag/reindex', undefined, { params }),

  getRagStatus: () =>
    apiClient.get<{ data: AIRagVectorStoreStatusDto }>('/api/ai/rag/status'),

  getRagMetrics: (params?: { organisationId?: string | null; subOrganisationId?: string | null }) =>
    apiClient.get<{ data: AIRagMetricsSummaryDto }>('/api/ai/rag/metrics', { params }),

  getRagJobs: (params?: AIRagIndexJobQueryDto) =>
    apiClient.get<{ data: PagedResult<AIRagIndexJobDto> }>('/api/ai/rag/jobs', { params }),

  getRagJob: (jobId: string) =>
    apiClient.get<{ data: AIRagIndexJobDto }>(`/api/ai/rag/jobs/${jobId}`),

  requeueRagJob: (jobId: string) =>
    apiClient.post(`/api/ai/rag/jobs/${jobId}/requeue`),

  getRagGraph: (params?: AIRagGraphRequestDto) =>
    apiClient.get<{ data: AIRagGraphDto }>('/api/ai/rag/graph', {
      params,
      // Use indexed collection keys so ASP.NET Core binds every selected value
      // (for example nodeTypes[0]=Complaint&nodeTypes[1]=Incident).
      paramsSerializer: { indexes: true },
    }),

  getSourceLibrary: (search?: string) =>
    apiClient.get<{ data: AISourceLibraryItemDto[] }>('/api/ai/source-library', {
      params: search ? { search } : undefined,
    }),

  createSourceLibraryItem: (request: CreateAISourceLibraryItemDto) =>
    apiClient.post<{ data: AISourceLibraryItemDto }>('/api/ai/source-library', request),

  deleteSourceLibraryItem: (sourceId: string) =>
    apiClient.delete(`/api/ai/source-library/${sourceId}`),

  getPromptLibrary: (search?: string) =>
    apiClient.get<{ data: AIPromptLibraryItemDto[] }>('/api/ai/prompt-library', {
      params: search ? { search } : undefined,
    }),

  createPromptLibraryItem: (request: CreateAIPromptLibraryItemDto) =>
    apiClient.post<{ data: AIPromptLibraryItemDto }>('/api/ai/prompt-library', request),

  updatePromptLibraryItem: (promptId: string, request: UpdateAIPromptLibraryItemDto) =>
    apiClient.put<{ data: AIPromptLibraryItemDto }>(`/api/ai/prompt-library/${promptId}`, request),

  deletePromptLibraryItem: (promptId: string) =>
    apiClient.delete(`/api/ai/prompt-library/${promptId}`),
};

export default aiApi;
